import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';

type TrackingCommand = ['setPath', string] | ['trackPageView'];

declare global {
  interface Window {
    _hsq?: TrackingCommand[];
    __portalHubSpotLastPath?: string;
    hsConversationsOnReady?: Array<() => void>;
    HubSpotConversations?: {
      clear(): void;
      widget: {
        status(): { loaded: boolean };
        load(): void;
        refresh(): void;
        remove(): void;
      };
    };
  }
}

export const HubSpotIntegration = () => {
  const { pathname, search, hash } = useLocation();
  const { loggedIn } = useUser();
  const lastWidgetUpdate = useRef(-Infinity);
  const wasLoggedIn = useRef(loggedIn);
  const clearChatSession = useRef(false);
  const hasRequestedWidgetLoad = useRef(false);

  useEffect(() => {
    if (window.__portalHubSpotLastPath === pathname) return;
    const queue = (window._hsq = window._hsq || []);
    queue.push(['setPath', pathname]);
    // public/index.html seeds the initial path; HubSpot tracks that view itself.
    if (window.__portalHubSpotLastPath !== undefined)
      queue.push(['trackPageView']);
    window.__portalHubSpotLastPath = pathname;
  }, [pathname]);

  useEffect(() => {
    if (wasLoggedIn.current && !loggedIn) clearChatSession.current = true;
    wasLoggedIn.current = loggedIn;
    let active = true;
    let timer: number | undefined;

    const syncWidget = () => {
      const sdk = window.HubSpotConversations;
      if (!active || !sdk) return;
      if (clearChatSession.current) {
        sdk.clear();
        clearChatSession.current = false;
      }
      if (!loggedIn) {
        // The SDK warns if remove is called before its first explicit load.
        if (hasRequestedWidgetLoad.current) sdk.widget.remove();
        return;
      }

      // HubSpot throttles load/refresh to once per second. Keep the latest URL
      // scheduled so rapid navigation still updates the final destination.
      const delay = Math.max(0, 1000 - (Date.now() - lastWidgetUpdate.current));
      if (delay > 0) {
        timer = window.setTimeout(syncWidget, delay);
        return;
      }
      lastWidgetUpdate.current = Date.now();
      if (sdk.widget.status().loaded) sdk.widget.refresh();
      else {
        hasRequestedWidgetLoad.current = true;
        sdk.widget.load();
      }
    };

    if (window.HubSpotConversations) syncWidget();
    else {
      const callbacks = (window.hsConversationsOnReady =
        window.hsConversationsOnReady || []);
      callbacks.push(syncWidget);
    }

    return () => {
      active = false;
      window.clearTimeout(timer);
      window.hsConversationsOnReady = window.hsConversationsOnReady?.filter(
        (callback) => callback !== syncWidget
      );
    };
  }, [loggedIn, pathname, search, hash]);

  useEffect(
    () => () => {
      if (hasRequestedWidgetLoad.current)
        window.HubSpotConversations?.widget.remove();
    },
    []
  );

  return null;
};
